'use client';

/**
 * Info modal for support chat - displays instructions and context about the system
 */

import React from 'react';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InfoModal({
  isOpen,
  onClose,
}: InfoModalProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            About Support Chat
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              fontSize: '20px',
              color: '#6b7280',
              lineHeight: 1,
              borderRadius: '4px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Close info"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '24px',
          }}
        >
          {/* Response Times Section */}
          <div
            style={{
              marginBottom: '24px',
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Response Times
            </h3>
            <div
              style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#374151',
                  lineHeight: '1.6',
                }}
              >
                Our support team monitors this chat during business hours. While we aim to respond promptly, response times may vary depending on the volume of requests and the complexity of your question. We appreciate your patience.
              </p>
            </div>
          </div>

          {/* Chat History Section */}
          <div
            style={{
              marginBottom: '24px',
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Chat History
            </h3>
            <div
              style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#374151',
                  lineHeight: '1.6',
                }}
              >
                Your chat history is stored in the browser you're currently using. If you switch browsers or clear your browser data, your previous conversations may not be accessible. For persistent access across devices and browsers, consider sharing your email address with us.
              </p>
            </div>
          </div>

          {/* Email Sharing Section */}
          <div
            style={{
              marginBottom: '24px',
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Email Sharing
            </h3>
            <div
              style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#374151',
                  lineHeight: '1.6',
                }}
              >
                If you share your email address with us, we can follow up with you via email if needed. This also helps us maintain context if you need to continue the conversation later or from a different device.
              </p>
            </div>
          </div>

          {/* Best Effort Basis Section */}
          <div>
            <h3
              style={{
                margin: 0,
                marginBottom: '12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Support Availability
            </h3>
            <div
              style={{
                padding: '16px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #fde68a',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#92400e',
                  lineHeight: '1.6',
                }}
              >
                This support chat is provided on a best-effort basis. While we strive to help with your questions and issues, we cannot guarantee immediate responses or availability. For urgent matters, please consider alternative contact methods.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

